<script setup lang="ts">
import { ref, watch } from "vue";

const {
  title,
  icon,
  isExpandable = true,
  hideToggleButton = false,
} = defineProps<{
  title: string;
  icon: string;
  isExpandable?: boolean;
  hideToggleButton?: boolean;
}>();

const emit = defineEmits<{
  expanded: [isExpanded: boolean];
}>();

const isExpanded = ref(false);

function expand() {
  isExpanded.value = true;
}

function collapse() {
  isExpanded.value = false;
}

watch(isExpanded, (newIsExpanded: boolean) => {
  emit("expanded", newIsExpanded);
});

defineExpose({
  expand,
  collapse,
});
</script>

<template>
  <div class="card expandable-card">
    <header>
      <div class="title">
        <i class="icon" :class="icon" />
        <span class="text-base-medium">{{ title }}</span>
      </div>

      <div class="actions">
        <slot name="actions"></slot>
      </div>
    </header>

    <div class="card-content">
      <slot></slot>
    </div>

    <div v-if="$slots.expandableContent" class="expandable-content">
      <Transition name="expand">
        <div v-if="isExpanded" class="inner">
          <div class="wrapper">
            <slot name="expandableContent"></slot>
          </div>
        </div>
      </Transition>

      <div
        v-if="isExpandable && !hideToggleButton"
        class="more text-sm-regular"
        @click="isExpanded = !isExpanded"
      >
        <span class="label">{{ isExpanded ? "See less" : "See more" }}</span>
        <i class="icon icon-chevron-right" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.expandable-card {
  display: flex;
  flex-direction: column;
  gap: var(--numbers-8);

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--numbers-4) var(--numbers-16);

    .title {
      display: flex;
      align-items: center;
      gap: var(--numbers-16);

      .icon {
        display: inline-block;
        width: var(--numbers-32);
        height: var(--numbers-32);
        border-radius: var(--numbers-6);
        background-color: var(--background-notice-subtle);
        color: var(--content-notice);
        line-height: var(--numbers-32);
        text-align: center;
      }
    }

    .actions {
      display: flex;
      height: var(--numbers-40);
      align-items: center;
      gap: var(--numbers-8);
    }
  }

  .expandable-content {
    .more {
      margin-left: var(--numbers-16);
      color: var(--content-brand);
      cursor: pointer;

      .icon {
        margin-left: var(--numbers-8);
        vertical-align: middle;
      }
    }

    .inner {
      display: grid;
      overflow: hidden;

      .wrapper {
        display: flex;
        overflow: hidden;
        min-height: 0;
        flex-direction: column;
        margin-bottom: var(--numbers-8);
        gap: var(--numbers-8);
      }
    }
  }
}

.expand-enter-active,
.expand-leave-active {
  overflow: hidden;
  transition:
    grid-template-rows var(--animation-duration) var(--animation-easing),
    opacity var(--animation-duration) var(--animation-easing);
}

.expand-enter-from,
.expand-leave-to {
  grid-template-rows: 0fr;
  opacity: 0;
}

.expand-enter-to,
.expand-leave-from {
  grid-template-rows: 1fr;
  opacity: 1;
}
</style>
